/* eslint-disable @typescript-eslint/no-explicit-any */
import { useMsal } from '@azure/msal-react';
import {
    Button,
    DialogActions,
    DialogBody,
    DialogContent,
    DialogSurface,
    DialogTitle,
    Drawer,
    DrawerBody,
    DrawerHeader,
    DrawerHeaderTitle,
    Input,
    Label,
    makeStyles,
    Select,
    Spinner,
    Switch,
    Table,
    TableBody,
    TableCell,
    TableCellLayout,
    TableHeader,
    TableHeaderCell,
    TableRow,
    tokens
} from '@fluentui/react-components';
import { DatePicker } from '@fluentui/react-datepicker-compat';
import { Add24Regular, Delete24Regular, Edit24Regular, Eye24Regular } from '@fluentui/react-icons';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ExcelExportButton from '../../components/ExcelExportButton';
import type { ArbeitszeitItem } from '../../models/ArbeitszeitItem';
import type { PlanwertSollstundenProjektmitarbeiterItem } from '../../models/PlanwertSollstundenProjektmitarbeiterItem';
import type { ProjektItem } from '../../models/ProjektItem';
import type { Projektrolle } from '../../models/ProjektRolle';
import { Projektrolle as ProjektrolleEnum } from '../../models/ProjektRolle';
import { DataService } from '../../services/dataService';
import type { ExcelExportColumn } from '../../utils/excelExport';

const useStyles = makeStyles({
    container: {
        padding: tokens.spacingHorizontalL,
        height: '100%',
        overflow: 'auto',
        display: 'flex',
        flexDirection: 'column',
    },
    filterSection: {
        display: 'flex',
        gap: tokens.spacingHorizontalL,
        alignItems: 'flex-end',
        marginBottom: tokens.spacingVerticalL,
        padding: tokens.spacingVerticalM,
        backgroundColor: tokens.colorNeutralBackground2,
        borderRadius: tokens.borderRadiusMedium,
        flexWrap: 'wrap',
    },
    filterGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalS,
    },
    filterLabel: {
        fontWeight: 600,
        fontSize: tokens.fontSizeBase300,
    },
    loadingContainer: {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px 20px',
        minHeight: '300px',
    },
    emptyState: {
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '20px',
        minHeight: '100px',
        color: tokens.colorNeutralForeground3,
        fontSize: tokens.fontSizeBase300,
        backgroundColor: tokens.colorNeutralBackground2,
        borderRadius: tokens.borderRadiusMedium,
        textAlign: 'center',
        margin: '10px',
    },
    tableContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalL,
        height: '100%',
        overflow: 'auto',
    },
    dynamicTableContainer: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalL,
        overflow: 'visible',
    },
    selectWidth: {
        minWidth: '200px',
    },
    dateInputWidth: {
        minWidth: '180px',
    },
    rightAlignedCell: {
        textAlign: 'right',
    },
    addButtonContainer: {
        marginBottom: tokens.spacingVerticalL,
    },
    formGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalM,
        marginBottom: tokens.spacingVerticalM,
    },
    formField: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalS,
    },
    actionCell: {
        display: 'flex',
        gap: tokens.spacingHorizontalS,
        justifyContent: 'center',
    },
    drawerFormGroup: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalL,
        padding: tokens.spacingVerticalM,
    },
    drawerField: {
        display: 'flex',
        flexDirection: 'column',
        gap: tokens.spacingVerticalXS,
    },
});

interface ProjektmanagementProjektstundenProps {
    project: ProjektItem | null;
}

interface TimeframeOption {
    label: string;
    value: string;
}

interface ProjektstundenRow {
    personalID: number;
    mitarbeiter: string;
    projektrolle: string;
    sollstunden: number;
    iststunden: number;
    differenz: number;
    aktiv: boolean;
}

interface PlanwertRow {
    mitarbeiter: string;
    von: Date;
    bis: Date;
    sollstunden: number;
}

const PROJEKTSTUNDEN_EXCEL_COLUMNS: ExcelExportColumn<ProjektstundenRow>[] = [
    { header: 'Employee', value: (row) => row.mitarbeiter, width: 30 },
    { header: 'Project Role', value: (row) => row.projektrolle, width: 22 },
    { header: 'Planned Hours', value: (row) => Number(row.sollstunden.toFixed(2)), width: 15 },
    { header: 'Actual Hours', value: (row) => Number(row.iststunden.toFixed(2)), width: 15 },
    { header: 'Difference', value: (row) => Number(row.differenz.toFixed(2)), width: 15 },
    { header: 'Active', value: (row) => (row.aktiv ? 'Yes' : 'No'), width: 10 },
];

const PLANWERTE_EXCEL_COLUMNS: ExcelExportColumn<PlanwertRow>[] = [
    { header: 'Employee', value: (row) => row.mitarbeiter, width: 30 },
    { header: 'From', value: (row) => new Date(row.von).toLocaleDateString('de-DE'), width: 12 },
    { header: 'To', value: (row) => new Date(row.bis).toLocaleDateString('de-DE'), width: 12 },
    { header: 'Planned Hours', value: (row) => Number(row.sollstunden.toFixed(2)), width: 15 },
];

// Helper functions
function parseDate(dateValue: any): Date | null {
    if (!dateValue) return null;
    const date = new Date(dateValue);
    if (isNaN(date.getTime())) return null;
    return date;
}

function isValidDate(date: Date | null): boolean {
    if (!date) return false;
    const time = date.getTime();
    return !isNaN(time) && time > 0;
}

function getProjektstart(projekt: ProjektItem | null): Date {
    if (!projekt) return new Date();

    let date = parseDate((projekt as any).operativerBeginnIst);
    if (isValidDate(date)) return date!;

    date = parseDate((projekt as any).operativerBeginnPlan);
    if (isValidDate(date)) return date!;

    return new Date();
}

function getProjektende(projekt: ProjektItem | null): Date {
    if (!projekt) return new Date();

    let date = parseDate((projekt as any).kaufmaennischesEnde);
    if (isValidDate(date)) return date!;

    date = parseDate((projekt as any).operativesEndeIst);
    if (isValidDate(date)) return date!;

    date = parseDate((projekt as any).operativesEndePlan);
    if (isValidDate(date)) return date!;

    return new Date();
}

function getWirtschaftsjahr(datum: Date): string {
    const monat = datum.getMonth() + 1;
    const jahr = datum.getFullYear();

    if (monat >= 9) {
        const nextYear = (jahr + 1) % 100;
        return `FY ${jahr}/${String(nextYear).padStart(2, '0')}`;
    } else {
        const prevYear = jahr - 1;
        const currYearSuffix = String(jahr % 100).padStart(2, '0');
        return `FY ${prevYear}/${currYearSuffix}`;
    }
}

function generateWirtschaftsjahrOptions(start: Date, end: Date): TimeframeOption[] {
    const options: TimeframeOption[] = [];
    const wirtschaftsjahre = new Set<string>();

    const current = new Date(start);
    while (current <= end) {
        const wj = getWirtschaftsjahr(current);
        if (!wirtschaftsjahre.has(wj)) {
            wirtschaftsjahre.add(wj);
            options.push({
                label: wj,
                value: wj.replace(/\s/g, ''),
            });
        }
        current.setMonth(current.getMonth() + 1);
    }

    return options;
}

function getRolleName(rolle: Projektrolle | undefined): string {
    if (!rolle && rolle !== 0) return '-';
    switch (rolle) {
        case ProjektrolleEnum.Projektleiter:
            return 'Project Manager';
        case ProjektrolleEnum.Projektleiterassistent:
            return 'Project Manager Assistant';
        case ProjektrolleEnum.Projektmitarbeiter:
            return 'Project Employee';
        case ProjektrolleEnum.Projektassistent:
            return 'Project Assistant';
        default:
            return '-';
    }
}

function calculateHoursForDateRange(
    arbeitszeiten: ArbeitszeitItem[],
    userId: number,
    dateVon?: Date,
    dateBis?: Date
): { sollstunden: number; iststunden: number } {
    const userItems = arbeitszeiten.filter(item => item.personalID === userId);

    let filteredItems = userItems;
    if (dateVon && dateBis) {
        filteredItems = userItems.filter(item => {
            const itemVon = parseDate(item.von);
            const itemBis = parseDate(item.bis);
            if (!isValidDate(itemVon) || !isValidDate(itemBis)) return false;

            // Check if item overlaps with date range
            return itemVon! <= dateBis && itemBis! >= dateVon;
        });
    }

    const sollstunden = 0;
    let iststunden = 0;

    filteredItems.forEach(item => {
        const itemVon = parseDate(item.von);
        const itemBis = parseDate(item.bis);

        if (!isValidDate(itemVon) || !isValidDate(itemBis)) return;

        // Calculate hours for this item (difference in hours)
        const hours = (itemBis!.getTime() - itemVon!.getTime()) / (1000 * 60 * 60);

        if (item.isReisezeit) {
            // Reisezeit counts as Iststunden
            iststunden += hours;
        } else {
            // Regular work time counts as Iststunden
            iststunden += hours;
        }
    });

    return { sollstunden, iststunden };
}

const ProjektmanagementProjektstunden: React.FC<ProjektmanagementProjektstundenProps> = ({ project }) => {
    const styles = useStyles();
    const { instance, accounts } = useMsal();
    const [arbeitszeiten, setArbeitszeiten] = useState<ArbeitszeitItem[]>([]);
    const [planwerte, setPlanwerte] = useState<PlanwertSollstundenProjektmitarbeiterItem[]>([]);
    const [projektmitarbeiter, setProjektmitarbeiter] = useState<any[]>([]);
    const [showPlanwerte, setShowPlanwerte] = useState(false);
    const [expandedGroups, setExpandedGroups] = useState<Set<string>>(new Set());
    const [isLoading, setIsLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);
    const [selectedOption, setSelectedOption] = useState<string>('gesamt');
    const [dateVon, setDateVon] = useState<Date | undefined>(undefined);
    const [dateBis, setDateBis] = useState<Date | undefined>(undefined);
    const [projektStart, setProjektStart] = useState<Date | null>(null);
    const [projektEnd, setProjektEnd] = useState<Date | null>(null);
    const [berichtstermine, setBerichtstermine] = useState<any[]>([]);

    const [timeframeOptions, setTimeframeOptions] = useState<TimeframeOption[]>([
        { label: 'Total', value: 'gesamt' },
        { label: 'Projektstart bis heute', value: 'startBisHeute' },
        { label: 'Benutzerdefinierter Zeitraum', value: 'custom' },
    ]);

    // Drawer and Dialog state
    const [isDrawerOpen, setIsDrawerOpen] = useState(false);
    const [drawerMode, setDrawerMode] = useState<'add' | 'view' | 'edit'>('add');
    const [selectedPlanwert, setSelectedPlanwert] = useState<PlanwertSollstundenProjektmitarbeiterItem | null>(null);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [planwertToDelete, setPlanwertToDelete] = useState<PlanwertSollstundenProjektmitarbeiterItem | null>(null);
    const [formData, setFormData] = useState({
        mitarbeiter: '',
        von: new Date(),
        bis: new Date(),
        sollstunden: 0,
    });

    // Create dataService instance using MSAL context
    const dataService = useMemo(() => {
        const accountInfo = accounts[0] || null;
        return accountInfo ? new DataService(instance, accountInfo) : null;
    }, [instance, accounts]);

    // Fetch arbeitszeiten data
    const fetchArbeitszeiten = useCallback(async () => {
        if (!project?.projektID || !dataService) {
            return;
        }

        try {
            setIsLoading(true);
            setError(null);

            const data = await dataService.getArbeitszeiten(project.projektID);
            console.log('Fetched arbeitszeiten data:', data);
            setArbeitszeiten(data || []);
        } catch (err) {
            console.error('Error loading arbeitszeiten data:', err);
            setError('Fehler beim Laden der Arbeitszeitdaten');
            setArbeitszeiten([]);
        } finally {
            setIsLoading(false);
        }
    }, [project?.projektID, dataService]);

    // Fetch planwerte data
    const fetchPlanwerte = useCallback(async () => {
        if (!project?.projektID || !dataService) {
            return;
        }

        try {
            const data = await dataService.getPlanwerteSollstundenProjektmitarbeiterListe(project.projektID);
            console.log('Fetched planwerte data:', data);
            setPlanwerte(data || []);
        } catch (err) {
            console.error('Error loading planwerte data:', err);
            setPlanwerte([]);
        }
    }, [project?.projektID, dataService]);

    // Fetch projekt members
    const fetchProjektmitarbeiter = useCallback(async () => {
        if (!project?.projektID || !dataService) {
            return;
        }

        try {
            const data = await dataService.getProjektmitarbeiterListe(project.projektID);
            console.log('Fetched projekt members:', data);
            setProjektmitarbeiter(data || []);
        } catch (err) {
            console.error('Error loading projekt members:', err);
            setProjektmitarbeiter([]);
        }
    }, [project?.projektID, dataService]);

    // Initialize timeframe options on project load
    useEffect(() => {
        if (!project) return;

        const start = getProjektstart(project);
        const end = getProjektende(project);

        setProjektStart(start);
        setProjektEnd(end);

        // Set default date range
        setDateVon(start);
        setDateBis(end);

        // Generate calendar years
        const calendarYears: TimeframeOption[] = [];
        for (let jahr = start.getFullYear(); jahr <= end.getFullYear(); jahr++) {
            calendarYears.push({
                label: jahr.toString(),
                value: `jahr_${jahr}`,
            });
        }

        // Generate Wirtschaftsjahr options
        const wirtschaftsjahrOptions = generateWirtschaftsjahrOptions(start, end);
        setTimeframeOptions([
            { label: 'Total', value: 'gesamt' },
            ...calendarYears,
            ...wirtschaftsjahrOptions,
            { label: 'Projektstart bis heute', value: 'startBisHeute' },
            { label: 'Benutzerdefinierter Zeitraum', value: 'custom' },
        ]);
    }, [project]);

    // Fetch report dates and add them to options
    useEffect(() => {
        if (!project?.projektID || !dataService) return;

        const fetchReportDates = async () => {
            try {
                const berichtstermine = await dataService.getBerichtsterminListe(project.projektID);
                if (berichtstermine && Array.isArray(berichtstermine)) {
                    // Store the reports in state for use in filtering
                    setBerichtstermine(berichtstermine);

                    setTimeframeOptions(prev => {
                        // First, remove any existing report options from previous state
                        const withoutReports = prev.filter(opt => !opt.value.startsWith('report_'));

                        // Create one option for each Berichtstermin
                        const reportOptions = (berichtstermine as any[]).map(item => ({
                            label: item.text || item.label,
                            value: `report_${item.value}`,
                        }));

                        // Insert report options after calendar/fiscal years, before project start to today
                        const startBisHeuteIndex = withoutReports.findIndex(opt => opt.value === 'startBisHeute');
                        if (startBisHeuteIndex !== -1) {
                            return [...withoutReports.slice(0, startBisHeuteIndex), ...reportOptions, ...withoutReports.slice(startBisHeuteIndex)];
                        }
                        return withoutReports;
                    });
                }
            } catch (err) {
                console.error('Error fetching report dates:', err);
            }
        };

        fetchReportDates();
    }, [project?.projektID, dataService]);

    // Fetch planwerte first, then arbeitszeiten and projekt members
    useEffect(() => {
        const fetchData = async () => {
            // Fetch planwerte first
            await fetchPlanwerte();
            // Then fetch arbeitszeiten
            await fetchArbeitszeiten();
            // And fetch projekt members
            await fetchProjektmitarbeiter();
        };
        fetchData();
    }, [project?.projektID, dataService, fetchArbeitszeiten, fetchPlanwerte, fetchProjektmitarbeiter]);

    // Create map of planwerte totals by mitarbeiter name
    const planwerteTotalsByMitarbeiter = useMemo(() => {
        const totals = new Map<string, number>();
        if (planwerte && planwerte.length > 0) {
            planwerte.forEach(item => {
                const mitarbeiterName = item.fullName || `${item.vorname} ${item.nachname}`;
                const current = totals.get(mitarbeiterName) || 0;
                totals.set(mitarbeiterName, current + (item.sollstunden || 0));
            });
        }
        return totals;
    }, [planwerte]);

    // Get filtered and grouped data
    const projektstundenData = useMemo(() => {
        if (!arbeitszeiten || arbeitszeiten.length === 0) {
            return [];
        }

        let filterDateVon: Date | undefined;
        let filterDateBis: Date | undefined;

        if (selectedOption === 'custom') {
            filterDateVon = dateVon;
            filterDateBis = dateBis;
        } else if (selectedOption === 'startBisHeute') {
            filterDateVon = projektStart || undefined;
            filterDateBis = new Date();
        } else if (selectedOption.startsWith('jahr_')) {
            // Calendar year (e.g., 'jahr_2026')
            const year = parseInt(selectedOption.replace('jahr_', ''), 10);
            filterDateVon = new Date(year, 0, 1);
            filterDateBis = new Date(year, 11, 31);
        } else if (selectedOption.startsWith('WJ')) {
            // Wirtschaftsjahr (fiscal year, Sept 1 - Aug 31)
            const yearPart = selectedOption.replace('WJ', '');
            const startYear = parseInt(yearPart.split('/')[0], 10);
            filterDateVon = new Date(startYear, 8, 1); // September 1
            filterDateBis = new Date(startYear + 1, 7, 31); // August 31 of next year
        } else if (selectedOption.startsWith('report_')) {
            // Report date - extract datumVon and datumBis from the selected report
            const reportId = selectedOption.replace('report_', '');
            const selectedReport = berichtstermine.find(r => r.value === reportId);

            if (selectedReport) {
                filterDateVon = parseDate(selectedReport.datumVon) || undefined;
                filterDateBis = parseDate(selectedReport.datumBis) || undefined;
            }
        }
        // For 'gesamt', no date filtering

        // Group arbeitszeiten by personalID
        const grouped = new Map<number, ArbeitszeitItem[]>();
        arbeitszeiten.forEach(item => {
            if (!grouped.has(item.personalID)) {
                grouped.set(item.personalID, []);
            }
            grouped.get(item.personalID)!.push(item);
        });

        // Create rows for each projekt member (including those without arbeitszeiten)
        const rows: ProjektstundenRow[] = [];

        // If we have projekt members, use them as the base
        if (projektmitarbeiter && projektmitarbeiter.length > 0) {
            projektmitarbeiter.forEach(member => {
                const mitarbeiterName = member.fullName || `${member.vorname} ${member.nachname}`;
                const personalID = member.personalID || member.userID;
                const items = grouped.get(personalID) || [];

                const hours = calculateHoursForDateRange(arbeitszeiten, personalID, filterDateVon, filterDateBis);
                // Use planwerte total sollstunden if available, otherwise use calculated sollstunden
                const sollstunden = planwerteTotalsByMitarbeiter.get(mitarbeiterName) || hours.sollstunden;
                const differenz = sollstunden - hours.iststunden;

                // Get role from team member
                const rolleAsText = getRolleName((member as any).rolle);

                // Determine if user is active in the timeframe
                let isActive = false;
                if (!filterDateVon || !filterDateBis) {
                    isActive = true;
                } else {
                    isActive = items.some(item => {
                        const itemVon = parseDate(item.von);
                        const itemBis = parseDate(item.bis);
                        if (!isValidDate(itemVon) || !isValidDate(itemBis)) return false;
                        return itemVon! <= filterDateBis! && itemBis! >= filterDateVon!;
                    });
                }

                rows.push({
                    personalID,
                    mitarbeiter: mitarbeiterName,
                    projektrolle: rolleAsText,
                    sollstunden,
                    iststunden: hours.iststunden,
                    differenz,
                    aktiv: isActive,
                });
            });
        } else {
            // Fallback: if no projekt members list, use arbeitszeiten
            grouped.forEach((items, personalID) => {
                if (items.length === 0) return;

                const firstItem = items[0];
                const mitarbeiterName = firstItem.fullName || `${firstItem.vorname} ${firstItem.nachname}`;
                const hours = calculateHoursForDateRange(arbeitszeiten, personalID, filterDateVon, filterDateBis);
                const sollstunden = planwerteTotalsByMitarbeiter.get(mitarbeiterName) || hours.sollstunden;
                const differenz = sollstunden - hours.iststunden;

                let isActive = false;
                if (!filterDateVon || !filterDateBis) {
                    isActive = true;
                } else {
                    isActive = items.some(item => {
                        const itemVon = parseDate(item.von);
                        const itemBis = parseDate(item.bis);
                        if (!isValidDate(itemVon) || !isValidDate(itemBis)) return false;
                        return itemVon! <= filterDateBis! && itemBis! >= filterDateVon!;
                    });
                }

                rows.push({
                    personalID,
                    mitarbeiter: mitarbeiterName,
                    projektrolle: getRolleName(undefined), // TODO: Get role from project team
                    sollstunden,
                    iststunden: hours.iststunden,
                    differenz,
                    aktiv: isActive,
                });
            });
        }

        // Sort by name
        rows.sort((a, b) => a.mitarbeiter.localeCompare(b.mitarbeiter));

        return rows;
    }, [arbeitszeiten, selectedOption, dateVon, dateBis, projektStart, planwerteTotalsByMitarbeiter, berichtstermine, projektmitarbeiter]);

    // Get planwerte data grouped by Mitarbeiter
    const planwerteData = useMemo(() => {
        if (!planwerte || planwerte.length === 0) {
            return [];
        }

        // Group by personalName (Mitarbeiter)
        const grouped = new Map<string, PlanwertRow[]>();

        planwerte.forEach(item => {
            const mitarbeiterName = item.fullName || `${item.vorname} ${item.nachname}`;

            if (!grouped.has(mitarbeiterName)) {
                grouped.set(mitarbeiterName, []);
            }

            const row: PlanwertRow = {
                mitarbeiter: mitarbeiterName,
                von: item.von,
                bis: item.bis,
                sollstunden: item.sollstunden || 0,
            };

            grouped.get(mitarbeiterName)!.push(row);
        });

        // Flatten the grouped data, keeping groups together
        const rows: PlanwertRow[] = [];
        const mitarbeiterNames = Array.from(grouped.keys()).sort();

        mitarbeiterNames.forEach(name => {
            const items = grouped.get(name) || [];
            // Sort items by 'von' date
            items.sort((a, b) => new Date(a.von).getTime() - new Date(b.von).getTime());
            rows.push(...items);
        });

        return rows;
    }, [planwerte]);

    // Get planwerte data organized by groups with expand/collapse functionality
    interface PlanwertGroup {
        mitarbeiter: string;
        items: PlanwertRow[];
        totalSollstunden: number;
        isExpanded: boolean;
    }

    const planwerteGroups = useMemo(() => {
        if (!planwerte || planwerte.length === 0) {
            return [];
        }

        // Group by personalName (Mitarbeiter)
        const grouped = new Map<string, PlanwertRow[]>();

        planwerte.forEach(item => {
            const mitarbeiterName = item.fullName || `${item.vorname} ${item.nachname}`;

            if (!grouped.has(mitarbeiterName)) {
                grouped.set(mitarbeiterName, []);
            }

            const row: PlanwertRow = {
                mitarbeiter: mitarbeiterName,
                von: item.von,
                bis: item.bis,
                sollstunden: item.sollstunden || 0,
            };

            grouped.get(mitarbeiterName)!.push(row);
        });

        // Create groups with totals
        const groups: PlanwertGroup[] = [];
        const mitarbeiterNames = Array.from(grouped.keys()).sort();

        mitarbeiterNames.forEach(name => {
            const items = grouped.get(name) || [];
            // Sort items by 'von' date
            items.sort((a, b) => new Date(a.von).getTime() - new Date(b.von).getTime());

            const totalSollstunden = items.reduce((sum, item) => sum + item.sollstunden, 0);

            groups.push({
                mitarbeiter: name,
                items,
                totalSollstunden,
                isExpanded: expandedGroups.has(name),
            });
        });

        return groups;
    }, [planwerte, expandedGroups]);

    // Auto-expand all groups when planwerte data loads
    useEffect(() => {
        if (planwerte && planwerte.length > 0) {
            // Get unique mitarbeiter names
            const mitarbeiterNames = new Set<string>();
            planwerte.forEach(item => {
                const mitarbeiterName = item.fullName || `${item.vorname} ${item.nachname}`;
                mitarbeiterNames.add(mitarbeiterName);
            });
            // Set all as expanded
            setExpandedGroups(new Set(mitarbeiterNames));
        }
    }, [planwerte]);

    const toggleGroupExpanded = useCallback((mitarbeiterName: string) => {
        setExpandedGroups(prev => {
            const newSet = new Set(prev);
            if (newSet.has(mitarbeiterName)) {
                newSet.delete(mitarbeiterName);
            } else {
                newSet.add(mitarbeiterName);
            }
            return newSet;
        });
    }, []);

    // Drawer and Dialog handlers
    const handleAddPlanwert = useCallback(() => {
        setFormData({
            mitarbeiter: '',
            von: projektStart || new Date(),
            bis: projektEnd || new Date(),
            sollstunden: 0,
        });
        setDrawerMode('add');
        setIsDrawerOpen(true);
    }, [projektStart, projektEnd]);

    const handleViewPlanwert = useCallback((item: PlanwertSollstundenProjektmitarbeiterItem) => {
        const mitarbeiterName = item.fullName || `${item.vorname} ${item.nachname}`;
        setFormData({
            mitarbeiter: mitarbeiterName,
            von: parseDate(item.von) || new Date(),
            bis: parseDate(item.bis) || new Date(),
            sollstunden: item.sollstunden || 0,
        });
        setSelectedPlanwert(item);
        setDrawerMode('view');
        setIsDrawerOpen(true);
    }, []);

    const handleEditPlanwert = useCallback((item: PlanwertSollstundenProjektmitarbeiterItem) => {
        const mitarbeiterName = item.fullName || `${item.vorname} ${item.nachname}`;
        setFormData({
            mitarbeiter: mitarbeiterName,
            von: parseDate(item.von) || new Date(),
            bis: parseDate(item.bis) || new Date(),
            sollstunden: item.sollstunden || 0,
        });
        setSelectedPlanwert(item);
        setDrawerMode('edit');
        setIsDrawerOpen(true);
    }, []);

    const handleDeletePlanwert = useCallback((item: PlanwertSollstundenProjektmitarbeiterItem) => {
        setPlanwertToDelete(item);
        setIsDeleteDialogOpen(true);
    }, []);

    const handleConfirmDelete = useCallback(async () => {
        if (!planwertToDelete || !dataService || !project?.projektID) return;

        try {
            setIsLoading(true);
            await dataService.deletePlanwertSollstundenProjektmitarbeiter(planwertToDelete.itemId || 0);
            setIsDeleteDialogOpen(false);
            setPlanwertToDelete(null);
            // Refresh planwerte data
            await fetchPlanwerte();
        } catch (err) {
            console.error('Error deleting planwert:', err);
            setError('Fehler beim Löschen der Planwerte');
        } finally {
            setIsLoading(false);
        }
    }, [planwertToDelete, dataService, project?.projektID, fetchPlanwerte]);

    const handleSavePlanwert = useCallback(async () => {
        if (!dataService || !project?.projektID) return;

        try {
            setIsLoading(true);

            // Find personalID from the selected mitarbeiter
            const selectedMitarbeiter = projektmitarbeiter.find(
                m => (m.fullName || `${m.vorname} ${m.nachname}`) === formData.mitarbeiter
            );
            const personalID = selectedMitarbeiter?.personalID || selectedMitarbeiter?.userID;

            // Format dates to YYYY-MM-DD using local date values (no timezone conversion)
            const formatDate = (date: Date): string => {
                const year = date.getFullYear();
                const month = String(date.getMonth() + 1).padStart(2, '0');
                const day = String(date.getDate()).padStart(2, '0');
                return `${year}-${month}-${day}`;
            };

            const vonStr = formatDate(formData.von);
            const bisStr = formatDate(formData.bis);

            const newItem: PlanwertSollstundenProjektmitarbeiterItem = {
                ...selectedPlanwert,
                vollName: formData.mitarbeiter,
                von: vonStr as any,
                bis: bisStr as any,
                sollstunden: formData.sollstunden,
                personalID: personalID,
                projektID: project.projektID,
            } as any;

            if (drawerMode === 'add') {
                await dataService.addPlanwertSollstundenProjektmitarbeiter(newItem);
            } else if (drawerMode === 'edit' && selectedPlanwert) {
                await dataService.updatePlanwertSollstundenProjektmitarbeiter(newItem);
            }

            setIsDrawerOpen(false);
            setSelectedPlanwert(null);
            // Refresh planwerte data
            await fetchPlanwerte();
        } catch (err) {
            console.error('Error saving planwert:', err);
            setError('Fehler beim Speichern der Planwerte');
        } finally {
            setIsLoading(false);
        }
    }, [selectedPlanwert, dataService, project, drawerMode, formData, fetchPlanwerte, projektmitarbeiter]);

    const handleCloseDrawer = useCallback(() => {
        setIsDrawerOpen(false);
        setSelectedPlanwert(null);
        setFormData({
            mitarbeiter: '',
            von: new Date(),
            bis: new Date(),
            sollstunden: 0,
        });
    }, []);

    if (!project) return null;

    return (
        <div className={styles.container}>
            {error && (
                <div className={styles.emptyState} style={{ color: tokens.colorStatusDangerForeground1 }}>
                    {error}
                </div>
            )}

            {/* Planned Values Toggle Section */}
            {!error && (
                <div className={styles.filterSection} style={{ marginBottom: tokens.spacingVerticalM }}>
                    <div className={styles.filterGroup}>
                        <label className={styles.filterLabel}>Show Planned Values</label>
                        <Switch checked={showPlanwerte} onChange={(_, data) => setShowPlanwerte(data.checked)} />
                    </div>
                </div>
            )}

            {showPlanwerte && !error && (
                <>
                    {planwerteData.length === 0 && !isLoading && (
                        <div className={styles.emptyState}>
                            No planned values available
                        </div>
                    )}

                    {planwerteGroups.length > 0 && !isLoading && (
                        <div className={styles.dynamicTableContainer} style={{ marginBottom: tokens.spacingVerticalXL }}>
                            <div className={styles.addButtonContainer} style={{ display: 'flex', gap: tokens.spacingHorizontalM }}>
                                <Button
                                    icon={<Add24Regular />}
                                    onClick={handleAddPlanwert}
                                    appearance="primary"
                                >
                                    Planwert hinzufügen
                                </Button>
                                <ExcelExportButton
                                    items={planwerteData}
                                    columns={PLANWERTE_EXCEL_COLUMNS}
                                    fileName={`PlannedValues_${project.projektID}_${new Date().toISOString().split('T')[0]}`}
                                    sheetName="Planned Values"
                                />
                            </div>
                            <h3 style={{ marginTop: 0, marginBottom: tokens.spacingVerticalM }}>Planned Values</h3>
                            <Table size="small">
                                <TableHeader>
                                    <TableRow>
                                        <TableHeaderCell style={{ width: '24px' }}></TableHeaderCell>
                                        <TableHeaderCell style={{ width: '120px', textAlign: 'center' }}>Actions</TableHeaderCell>
                                        <TableHeaderCell>Employee</TableHeaderCell>
                                        <TableHeaderCell>From</TableHeaderCell>
                                        <TableHeaderCell>To</TableHeaderCell>
                                        <TableHeaderCell><div style={{ textAlign: 'right', width: '100%' }}>Planned Hours</div></TableHeaderCell>
                                    </TableRow>
                                </TableHeader>
                                <TableBody>
                                    {planwerteGroups.map((group) => (
                                        <React.Fragment key={group.mitarbeiter}>
                                            {/* Group Header Row */}
                                            <TableRow
                                                style={{
                                                    backgroundColor: tokens.colorNeutralBackground3,
                                                    cursor: 'pointer',
                                                    fontWeight: 600,
                                                }}
                                                onClick={() => toggleGroupExpanded(group.mitarbeiter)}
                                            >
                                                <TableCell>
                                                    <TableCellLayout>
                                                        {group.isExpanded ? '▼' : '▶'}
                                                    </TableCellLayout>
                                                </TableCell>
                                                <TableCell></TableCell>
                                                <TableCell>
                                                    <TableCellLayout>{group.mitarbeiter}</TableCellLayout>
                                                </TableCell>
                                                <TableCell></TableCell>
                                                <TableCell></TableCell>
                                                <TableCell>
                                                    <TableCellLayout style={{ justifyContent: 'flex-end' }}>
                                                        {group.totalSollstunden.toFixed(2)}
                                                    </TableCellLayout>
                                                </TableCell>
                                            </TableRow>

                                            {/* Group Items */}
                                            {group.isExpanded &&
                                                group.items.map((row, index) => {
                                                    // Find the original planwert item for this row
                                                    const originalItem = planwerte.find(
                                                        p => (p.fullName || `${p.vorname} ${p.nachname}`) === row.mitarbeiter &&
                                                            parseDate(p.von)?.getTime() === new Date(row.von).getTime()
                                                    );

                                                    return (
                                                        <TableRow key={`${group.mitarbeiter}-item-${index}`}>
                                                            <TableCell></TableCell>
                                                            <TableCell>
                                                                <TableCellLayout className={styles.actionCell}>
                                                                    <Button
                                                                        icon={<Eye24Regular />}
                                                                        appearance="subtle"
                                                                        size="small"
                                                                        onClick={() => originalItem && handleViewPlanwert(originalItem)}
                                                                        title="View"
                                                                    />
                                                                    <Button
                                                                        icon={<Edit24Regular />}
                                                                        appearance="subtle"
                                                                        size="small"
                                                                        onClick={() => originalItem && handleEditPlanwert(originalItem)}
                                                                        title="Edit"
                                                                    />
                                                                    <Button
                                                                        icon={<Delete24Regular />}
                                                                        appearance="subtle"
                                                                        size="small"
                                                                        onClick={() => originalItem && handleDeletePlanwert(originalItem)}
                                                                        title="Delete"
                                                                    />
                                                                </TableCellLayout>
                                                            </TableCell>
                                                            <TableCell>
                                                                <TableCellLayout style={{ paddingLeft: tokens.spacingHorizontalL }}>
                                                                    {row.mitarbeiter}
                                                                </TableCellLayout>
                                                            </TableCell>
                                                            <TableCell>
                                                                <TableCellLayout>{new Date(row.von).toLocaleDateString('de-DE')}</TableCellLayout>
                                                            </TableCell>
                                                            <TableCell>
                                                                <TableCellLayout>{new Date(row.bis).toLocaleDateString('de-DE')}</TableCellLayout>
                                                            </TableCell>
                                                            <TableCell>
                                                                <TableCellLayout style={{ justifyContent: 'flex-end' }}>
                                                                    {row.sollstunden.toFixed(2)}
                                                                </TableCellLayout>
                                                            </TableCell>
                                                        </TableRow>
                                                    );
                                                })}

                                            {/* Total Row */}
                                            {group.isExpanded && (
                                                <TableRow
                                                    style={{
                                                        backgroundColor: tokens.colorNeutralBackground2,
                                                        fontWeight: 600,
                                                    }}
                                                >
                                                    <TableCell></TableCell>
                                                    <TableCell></TableCell>
                                                    <TableCell>
                                                        <TableCellLayout style={{ paddingLeft: tokens.spacingHorizontalL }}>
                                                            Total
                                                        </TableCellLayout>
                                                    </TableCell>
                                                    <TableCell></TableCell>
                                                    <TableCell></TableCell>
                                                    <TableCell>
                                                        <TableCellLayout style={{ justifyContent: 'flex-end' }}>
                                                            {group.totalSollstunden.toFixed(2)}
                                                        </TableCellLayout>
                                                    </TableCell>
                                                </TableRow>
                                            )}
                                        </React.Fragment>
                                    ))}
                                </TableBody>
                            </Table>
                        </div>
                    )}
                </>
            )}

            {!error && projektstundenData.length === 0 && !isLoading && (
                <div className={styles.emptyState}>
                    No work time data available
                </div>
            )}

            {/* Time Period Filter Section for Work Time Data Table */}
            {!error && (
                <div className={styles.filterSection}>
                    <div className={styles.filterGroup}>
                        <label className={styles.filterLabel}>Zeitraum</label>
                        <Select
                            value={selectedOption}
                            onChange={(_e, data) => {
                                setSelectedOption(data.value);
                            }}
                            className={styles.selectWidth}
                        >
                            {timeframeOptions.map(option => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </Select>
                    </div>

                    {selectedOption === 'custom' && (
                        <>
                            <div className={styles.filterGroup}>
                                <label className={styles.filterLabel}>Von</label>
                                <DatePicker
                                    value={dateVon}
                                    onSelectDate={(date: Date | null | undefined) => setDateVon(date ?? undefined)}
                                    placeholder="Von Datum"
                                    className={styles.dateInputWidth}
                                    minDate={projektStart || undefined}
                                    maxDate={projektEnd || undefined}
                                />
                            </div>

                            <div className={styles.filterGroup}>
                                <label className={styles.filterLabel}>Bis</label>
                                <DatePicker
                                    value={dateBis}
                                    onSelectDate={(date: Date | null | undefined) => setDateBis(date ?? undefined)}
                                    placeholder="Bis Datum"
                                    className={styles.dateInputWidth}
                                    minDate={projektStart || undefined}
                                    maxDate={projektEnd || undefined}
                                />
                            </div>
                        </>
                    )}
                    <ExcelExportButton
                        items={isLoading ? [] : projektstundenData}
                        columns={PROJEKTSTUNDEN_EXCEL_COLUMNS}
                        fileName={`ProjectHours_${project.projektID}_${new Date().toISOString().split('T')[0]}`}
                        sheetName="Project Hours"
                    />
                </div>
            )}

            {!isLoading ? (
                <div className={styles.tableContainer}>
                    <Table size="small">
                        <TableHeader>
                            <TableRow>
                                <TableHeaderCell>Employee</TableHeaderCell>
                                <TableHeaderCell>Project Role</TableHeaderCell>
                                <TableHeaderCell><div style={{ textAlign: 'right', width: '100%' }}>Planned Hours</div></TableHeaderCell>
                                <TableHeaderCell><div style={{ textAlign: 'right', width: '100%' }}>Actual Hours</div></TableHeaderCell>
                                <TableHeaderCell><div style={{ textAlign: 'right', width: '100%' }}>Difference</div></TableHeaderCell>
                                <TableHeaderCell>Active</TableHeaderCell>
                            </TableRow>
                        </TableHeader>
                        <TableBody>
                            {projektstundenData.map((row) => (
                                <TableRow key={row.personalID}>
                                    <TableCell>
                                        <TableCellLayout>{row.mitarbeiter}</TableCellLayout>
                                    </TableCell>
                                    <TableCell>
                                        <TableCellLayout>{row.projektrolle}</TableCellLayout>
                                    </TableCell>
                                    <TableCell>
                                        <TableCellLayout style={{ justifyContent: 'flex-end' }}>
                                            {row.sollstunden.toFixed(2)}
                                        </TableCellLayout>
                                    </TableCell>
                                    <TableCell>
                                        <TableCellLayout style={{ justifyContent: 'flex-end' }}>
                                            {row.iststunden.toFixed(2)}
                                        </TableCellLayout>
                                    </TableCell>
                                    <TableCell>
                                        <TableCellLayout style={{ justifyContent: 'flex-end' }}>
                                            {row.differenz.toFixed(2)}
                                        </TableCellLayout>
                                    </TableCell>
                                    <TableCell>
                                        <TableCellLayout>
                                            {row.aktiv ? 'Yes' : 'No'}
                                        </TableCellLayout>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </div>
            ) : (
                <div className={styles.loadingContainer}>
                    <Spinner label="Loading work time data..." />
                </div>
            )}

            {/* Planwert Drawer */}
            <Drawer open={isDrawerOpen} onOpenChange={(_, { open }) => !open && handleCloseDrawer()} position="end" size="medium">
                <DrawerHeader>
                    <DrawerHeaderTitle>
                        {drawerMode === 'add' && 'Planwert hinzufügen'}
                        {drawerMode === 'view' && 'Planwert anzeigen'}
                        {drawerMode === 'edit' && 'Planwert bearbeiten'}
                    </DrawerHeaderTitle>
                </DrawerHeader>
                <DrawerBody>
                    <div className={styles.drawerFormGroup}>
                        <div className={styles.drawerField}>
                            <Label weight="semibold">Mitarbeiter *</Label>
                            {drawerMode === 'view' ? (
                                <div>{formData.mitarbeiter}</div>
                            ) : (
                                <Select
                                    value={formData.mitarbeiter}
                                    onChange={(_, data) => setFormData({ ...formData, mitarbeiter: data.value })}
                                    disabled={drawerMode !== 'add' && drawerMode !== 'edit' ? false : false}
                                >
                                    {projektmitarbeiter.map(member => {
                                        const name = member.fullName || `${member.vorname} ${member.nachname}`;
                                        return (
                                            <option key={member.personalID} value={name}>
                                                {name}
                                            </option>
                                        );
                                    })}
                                </Select>
                            )}
                        </div>

                        <div className={styles.drawerField}>
                            <Label weight="semibold">Von *</Label>
                            {drawerMode === 'view' ? (
                                <div>{formData.von.toLocaleDateString('de-DE')}</div>
                            ) : (
                                <DatePicker
                                    value={formData.von}
                                    onSelectDate={(date) => date && setFormData({ ...formData, von: date })}
                                />
                            )}
                        </div>

                        <div className={styles.drawerField}>
                            <Label weight="semibold">Bis *</Label>
                            {drawerMode === 'view' ? (
                                <div>{formData.bis.toLocaleDateString('de-DE')}</div>
                            ) : (
                                <DatePicker
                                    value={formData.bis}
                                    onSelectDate={(date) => date && setFormData({ ...formData, bis: date })}
                                />
                            )}
                        </div>

                        <div className={styles.drawerField}>
                            <Label weight="semibold">Sollstunden *</Label>
                            {drawerMode === 'view' ? (
                                <div>{formData.sollstunden.toFixed(2)}</div>
                            ) : (
                                <Input
                                    type="number"
                                    value={formData.sollstunden.toString()}
                                    onChange={(_, data) => setFormData({ ...formData, sollstunden: parseFloat(data.value) || 0 })}
                                />
                            )}
                        </div>

                        <div style={{ display: 'flex', gap: tokens.spacingHorizontalM, justifyContent: 'flex-end', marginTop: tokens.spacingVerticalL }}>
                            {drawerMode === 'view' ? (
                                <Button onClick={handleCloseDrawer} appearance="secondary">
                                    Schließen
                                </Button>
                            ) : (
                                <>
                                    <Button onClick={handleCloseDrawer} appearance="secondary">
                                        Abbrechen
                                    </Button>
                                    <Button onClick={handleSavePlanwert} appearance="primary" disabled={isLoading}>
                                        Änderungen speichern
                                    </Button>
                                </>
                            )}
                        </div>
                    </div>
                </DrawerBody>
            </Drawer>

            {/* Delete Confirmation Dialog */}
            {isDeleteDialogOpen && planwertToDelete && (
                <DialogSurface>
                    <DialogBody>
                        <DialogTitle>Planwert löschen</DialogTitle>
                        <DialogContent>
                            Möchten Sie diesen Planwert wirklich löschen?<br />
                            <strong>{planwertToDelete.fullName || `${planwertToDelete.vorname} ${planwertToDelete.nachname}`}</strong>
                        </DialogContent>
                        <DialogActions>
                            <Button onClick={() => setIsDeleteDialogOpen(false)} appearance="secondary">
                                Abbrechen
                            </Button>
                            <Button onClick={handleConfirmDelete} appearance="primary" disabled={isLoading}>
                                Löschen
                            </Button>
                        </DialogActions>
                    </DialogBody>
                </DialogSurface>
            )}
        </div>
    );
};

export default ProjektmanagementProjektstunden;
